/**
 * 2026 생기부 기재요령 완전 정리 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 생기부 기재요령, 2026 생기부, 생기부 작성법, 생기부 예시
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import '../../styles/design-system/index.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

const Saenggibu2026GuidePage = () => {
  const navigate = useNavigate();

  const keyChanges = [
    {
      category: '교과학습발달상황',
      changes: [
        '세부능력 및 특기사항: 과목별 500자 이내 (고등)',
        '학생 성장 중심 기술 강화',
        '진로연계 학습활동 기록 권장'
      ]
    },
    {
      category: '행동특성 및 종합의견',
      changes: [
        '학년 단위 300자 이내 (2025학년도 500자에서 축소)',
        '핵심역량 중심 종합 기술',
        '학생의 변화·성장 과정 기록'
      ]
    },
    {
      category: '창의적 체험활동',
      changes: [
        '자율·자치활동(3학년은 자율활동): 500자 이내',
        '동아리활동: 500자 이내',
        '진로활동: 500자 이내 (2025학년도 700자에서 축소)'
      ]
    },
    {
      category: '생성형 AI 활용',
      changes: [
        'AI 생성 자료를 서술형 항목에 그대로 입력하지 않기',
        '윤문 등 보조 수단으로 활용 가능',
        '교사가 실제 수행·허위 과장·기재요령을 최종 확인'
      ]
    }
  ];

  const commonMistakes = [
    { mistake: '브랜드명/상표명 기재', correct: '일반 명칭으로 변경 (예: 레고→블록)' },
    { mistake: '특정 기관/대회명 기재', correct: '대회 유형으로 기술 (예: 전국대회→교내대회)' },
    { mistake: '자소서식 표현 사용', correct: '객관적 사실 중심 기술' },
    { mistake: '종결어미 오류 (-습니다)', correct: '-함, -임 으로 통일' },
    { mistake: '외모/신체 관련 표현', correct: '학습·활동 역량 중심 기술' }
  ];

  return (
    <>
      <Helmet>
        <title>2026 생기부 기재요령 완전정리 | 교사용 핵심 변경사항·위반사례·예시</title>
        <meta name="description" content="2026년 생기부 기재요령 완전 정리. 교과세특, 행발, 창체 글자수 변경사항. 흔한 위반사례 5가지와 올바른 작성법. AI 생기부 검사기로 기재요령 위반 자동 체크." />
        <meta name="keywords" content="생기부 기재요령, 2026 생기부, 생기부 기재요령 2026, 생기부 작성법, 생기부 예시, 생기부 글자수, 세특 기재요령, 행발 기재요령, 창체 기재요령, 생기부 위반사례" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/saenggibu-2026" />
        
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/saenggibu-2026" />
        <meta property="og:title" content="2026 생기부 기재요령 완전정리 | 변경사항·위반사례·예시" />
        <meta property="og:description" content="2026년 생기부 기재요령 핵심 정리. 위반사례 자동 검사 기능 제공." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-saenggibu-2026.png" />
        <meta property="og:locale" content="ko_KR" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "2026 생기부 기재요령에 맞게 작성하는 방법",
            "description": "2026년 교육부 생기부 기재요령에 따른 올바른 생활기록부 작성법",
            "step": [
              { "@type": "HowToStep", "name": "글자수 확인", "text": "영역별 글자수 제한 확인 (세특 500자, 행발 300자 등)" },
              { "@type": "HowToStep", "name": "금지표현 검토", "text": "브랜드명, 대회명, 자소서식 표현 제거" },
              { "@type": "HowToStep", "name": "종결어미 통일", "text": "-함, -임 으로 종결어미 통일" },
              { "@type": "HowToStep", "name": "AI 검사", "text": "왓퀴즈 생기부 검사기로 위반사항 자동 체크" }
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "2026년 생기부 세특 글자수는?",
                "acceptedAnswer": { "@type": "Answer", "text": "고등학교 기준 과목별 500자 이내입니다. 학생의 성장과 변화를 중심으로 기술하며, 진로연계 학습활동 기록이 권장됩니다." }
              },
              {
                "@type": "Question",
                "name": "생기부에 브랜드명을 쓰면 안 되나요?",
                "acceptedAnswer": { "@type": "Answer", "text": "네, 특정 브랜드명/상표명은 기재가 금지됩니다. 예를 들어 '레고'는 '블록', '포토샵'은 '이미지 편집 프로그램'으로 기재해야 합니다." }
              },
              {
                "@type": "Question",
                "name": "생기부 기재요령 위반 여부를 어떻게 확인하나요?",
                "acceptedAnswer": { "@type": "Answer", "text": "왓퀴즈 생기부 검사기를 사용하면 기재요령 위반사항을 AI가 자동으로 검사하고, 수정 제안까지 받을 수 있습니다." }
              },
              {
                "@type": "Question",
                "name": "학생부 상업적 이용 제한 법 시행 후 교사가 AI 생기부 도구를 사용해도 되나요?",
                "acceptedAnswer": { "@type": "Answer", "text": "교사가 직접 관찰·평가한 자료를 바탕으로 초안을 만들고 최종 내용을 직접 검토하는 통상적인 업무 보조 이용은, 학생부를 취득해 입시 상담·판매·유료 자료·AI 학습 데이터 등 영업 목적으로 이용하는 교육부의 주요 위반 사례와 다릅니다. AI 결과물을 그대로 입력하지 말고 실제 수행과 기재요령을 교사가 최종 확인해야 합니다." }
              }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #ecfdf5 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#d1fae5',
              color: '#047857',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              2026년 교육부 기재요령 반영
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              생기부 기재요령<br />
              <span style={{ color: '#059669' }}>완전 정리</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              변경사항, 흔한 위반사례, 올바른 작성법까지<br />
              <strong>한 페이지로 끝내세요</strong>
            </p>

            <button
              onClick={() => navigate('/saenggibu-inspector')}
              style={{
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#059669',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              내 생기부 기재요령 검사하기 →
            </button>
          </div>
        </section>

        {/* GEO 핵심 답변 */}
        <section style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'normal',
          border: 0
        }}>
          <div style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '28px'
          }}>
            <span style={{ display: 'inline-block', fontSize: '13px', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>
              핵심 답변
            </span>
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#111', margin: '0 0 12px' }}>
              2026 생기부 기재요령이란?
            </h2>
            <p style={{ fontSize: '16px', lineHeight: '1.75', color: '#334155', margin: '0 0 18px' }}>
              2026 생기부 기재요령은 교사가 생활기록부에 학생의 학습 과정, 성장, 역량을 어떤 범위와 표현으로 기록할지 정하는 작성 기준입니다. 교사는 글자수, 금지 표현, 객관적 사실 중심 서술, 종결어미를 함께 확인해야 합니다.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              {[
                { label: '확인 1', value: '영역별 글자수와 항목 구분' },
                { label: '확인 2', value: '브랜드명·외부대회명 등 금지 표현' },
                { label: '확인 3', value: '학생 성장 중심의 객관적 서술' },
              ].map((item) => (
                <div key={item.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px' }}>
                  <strong style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>{item.label}</strong>
                  <span style={{ fontSize: '14px', color: '#111', lineHeight: '1.5' }}>{item.value}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: '16px 0 0' }}>
              기준: 교육부 학교생활기록부 기재요령을 교사 업무 관점으로 정리한 왓퀴즈 내부 가이드
            </p>
          </div>
        </section>

        {/* 주요 변경사항 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            2026년 주요 변경사항
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {keyChanges.map((item, idx) => (
              <div key={idx} style={{
                padding: '24px',
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#059669',
                  marginBottom: '16px'
                }}>
                  {item.category}
                </h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0
                }}>
                  {item.changes.map((change, i) => (
                    <li key={i} style={{
                      padding: '8px 0',
                      fontSize: '14px',
                      color: '#374151',
                      borderBottom: i < item.changes.length - 1 ? '1px solid #e5e7eb' : 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      <span style={{ color: '#059669' }}>•</span>
                      {change}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* 흔한 위반사례 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#fef2f2'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              흔한 위반사례 TOP 5
            </h2>
            <p style={{
              textAlign: 'center',
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '32px'
            }}>
              아래 사례는 모두 기재요령 위반입니다
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {commonMistakes.map((item, idx) => (
                <div key={idx} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    borderLeft: '4px solid #ef4444'
                  }}>
                    <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>이렇게 쓰면 안 돼요</span>
                    <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#374151' }}>{item.mistake}</p>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '8px',
                    borderLeft: '4px solid #22c55e'
                  }}>
                    <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>이렇게 써야 해요</span>
                    <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#374151' }}>{item.correct}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              기재요령 위반,<br />
              <span style={{ color: '#3182f6' }}>AI가 자동으로 잡아드려요</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              생기부 문장을 붙여넣기만 하면<br />
              기재요령 위반사항을 <strong>1초 만에 검사</strong>해드립니다.
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center'
            }}>
              <button
                onClick={() => navigate('/saenggibu-inspector')}
                style={{
                  padding: '16px 40px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: '#3182f6',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                무료로 생기부 검사하기
              </button>
              
              <button
                onClick={() => navigate('/create-saenggibu')}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#3182f6',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                AI로 생기부 자동 작성하기 →
              </button>
            </div>

            <p style={{
              marginTop: '24px',
              fontSize: '13px',
              color: '#94a3b8'
            }}>
              2026 기재요령 100% 반영 · {TEACHER_COUNT_LABEL} 이상 교사 사용 중
            </p>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/edzip" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              에듀집 완전정리
            </Link>
            <Link to="/guide/learning-software" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              학습지원 소프트웨어 기준
            </Link>
            <Link to="/create-saenggibu" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              AI 생기부 자동 작성
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Saenggibu2026GuidePage;
